// Pairing a PC to a station (F-STN-02, spec/07 §5, spec/10 §3.2): the PC shows an 8-character code
// full-screen; staff type it here, and approving binds the PC's device to the station
// (`approveDevicePairing`, `mode: 'existing'`). Used by the list's pairing dialog and by the last step
// of adding a station. Step one's installers (Clavian Station, Clavian Counter; F-STN-17) are offered right here as
// downloads, so no one needs a command line to set up a PC.

import { useTranslation } from '@clavian/i18n/react';
import { Button, TextField } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { InstallerDownloads } from './InstallerDownloads';
import { isCompletePairingCode, normalizePairingCode } from './pairing';
import { useApproveDevicePairing } from './queries';

/** The pairing code and its approval (`mode: 'existing'`): binds the PC's device to the station. */
export function PairingForm({
  organizationId,
  venueId,
  stationId,
  stationNumber,
  onPaired,
  secondary,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly stationId: string;
  readonly stationNumber: number;
  readonly onPaired: () => void;
  /** A second action beside Approve: "Pair later" in the add-a-station flow. */
  readonly secondary?: ReactNode;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const approve = useApproveDevicePairing(organizationId, venueId);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const normalized = normalizePairingCode(code);
    if (!isCompletePairingCode(normalized)) {
      setError(t('stations.pairing.codeInvalid'));
      return;
    }
    setError(undefined);
    approve.mutate(
      { code: normalized, body: { venueId, mode: 'existing', stationId } },
      {
        onSuccess: () => {
          setCode('');
          onPaired();
        },
        onError: (mutationError) => {
          const code = problemCode(mutationError);
          setError(
            code === PROBLEM.notFound
              ? t('stations.pairing.codeNotFound')
              : code === PROBLEM.conflict
                ? t('stations.pairing.conflict')
                : problem.text(mutationError, { fallback: t('stations.pairing.refused') }),
          );
        },
      },
    );
  }

  return (
    <form className="console-form" aria-label={t('stations.pairing.formLabel', { number: stationNumber })} noValidate onSubmit={submit}>
      <ol className="console-pairing__steps">
        <li>{t('stations.pairing.stepOne')}</li>
        <li>{t('stations.pairing.stepTwo')}</li>
      </ol>
      <InstallerDownloads organizationId={organizationId} venueId={venueId} />
      <TextField
        label={t('stations.pairing.codeLabel')}
        description={t('stations.pairing.codeHint')}
        required
        className="console-pairing__code"
        value={code}
        autoComplete="off"
        onChange={(event) => setCode(normalizePairingCode(event.target.value))}
        error={error}
      />
      <div className="console-form__actions">
        <Button type="submit" variant="primary" busy={approve.isPending}>
          {t('stations.pairing.approve')}
        </Button>
        {secondary}
      </div>
    </form>
  );
}
